import { api, ApiTags } from '../../api';

import {
    CourseRequest,
    CourseResponse,
    ChaptersRequest,
    ChaptersResponse,
    ChapterRequest,
    ChapterResponse,
    CoursesByIdsRequest,
    CourseCardsResponse,
} from './types';
import { transformCoursesResponseToCards, transformChaptersResponse, transformChapterResponse } from './mappers';

const courseApi = api.injectEndpoints({
    endpoints: build => ({
        getCourses: build.query<CourseCardsResponse, void>({
            query: () => '/courses',
            transformResponse: transformCoursesResponseToCards,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Courses, id: 'LIST' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Courses, id })),
                    { type: ApiTags.Courses, id: 'LIST' },
                ];
            },
        }),
        getCourse: build.query<CourseResponse, CourseRequest>({
            query: ({ courseId }) => `/courses/${courseId}`,
            providesTags: (result, _, request) => (result ? [{ type: ApiTags.Courses, id: request.courseId }] : []),
        }),
        getChapters: build.query<ChaptersResponse, ChaptersRequest>({
            query: ({ courseId }) => `/courses/${courseId}/chapters`,
            transformResponse: transformChaptersResponse,
            providesTags: (result, _, request) => {
                if (!result?.chapters) {
                    return [{ type: ApiTags.Courses, id: `CHAPTERS_${request.courseId}` }];
                }

                return [
                    ...result.chapters.map(({ id }) => ({ type: ApiTags.Courses, id: `CHAPTER_${id}` })),
                    { type: ApiTags.Courses, id: `CHAPTERS_${request.courseId}` },
                ];
            },
        }),
        getChapter: build.query<ChapterResponse, ChapterRequest>({
            query: ({ courseId, orderNumber }) => `/courses/${courseId}/chapters/${orderNumber}`,
            transformResponse: transformChapterResponse,
            providesTags: (result, _, request) =>
                result ? [{ type: ApiTags.Courses, id: `CHAPTER_${request.courseId}_${request.orderNumber}` }] : [],
        }),
        getCoursesByIds: build.query<CourseCardsResponse, CoursesByIdsRequest>({
            query: ({ ids }) => ({
                url: '/courses/by-ids',
                params: { ids },
            }),
            transformResponse: transformCoursesResponseToCards,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Courses, id: 'LIST' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Courses, id })),
                    { type: ApiTags.Courses, id: 'LIST' },
                ];
            },
        }),
    }),
});

export const {
    useGetCoursesQuery,
    useGetCourseQuery,
    useGetChaptersQuery,
    useGetChapterQuery,
    useGetCoursesByIdsQuery,
} = courseApi;
